import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterLuxuryHomesPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&minPrice=1000000&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster Luxury Homes | Premium Properties | Truc Doan</title>
        <meta name="description" content="Explore luxury homes in Westminster, CA. Premium properties featuring newer construction, upgraded finishes, and spacious floor plans in Westminster's finest neighborhoods. Contact Truc Doan." />
        <meta name="keywords" content="Westminster luxury homes, premium homes Westminster CA, luxury real estate Westminster, million dollar homes Westminster, new construction Westminster, upgraded homes Westminster, Truc Doan luxury realtor" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-luxury-homes`} />
        <meta property="og:title" content="Westminster Luxury Homes | Premium Properties | Truc Doan" />
        <meta property="og:description" content="Discover Westminster's finest luxury homes. Premium properties with upgraded finishes, newer construction, and spacious layouts." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-luxury-homes`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/westminster-luxury-homes`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan specializes in luxury homes in Westminster, CA. Premium properties with upgraded finishes and newer construction.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster Luxury Homes for Sale</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Discover premium properties in Westminster's finest neighborhoods — newer construction, upgraded finishes, and exceptional living.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Luxury Living in Westminster</h2>
          <p>Westminster's luxury market features stunning newer-construction homes, extensively remodeled properties, and spacious estates that rival neighboring Huntington Beach and Fountain Valley. These premium homes offer modern open floor plans, designer kitchens, smart home technology, and resort-style outdoor spaces.</p>
          <p>From custom-built residences in quiet cul-de-sacs to fully upgraded homes near top-rated schools, Westminster's luxury segment offers exceptional value compared to coastal Orange County communities — without sacrificing quality or amenities.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Westminster Luxury Home Features</h2>
          <p>Premium properties in Westminster offer these sought-after features:</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Newer Construction</h3>
              <p>Homes built in the last 5–10 years featuring modern architecture, energy efficiency, and contemporary floor plans with 4–6 bedrooms.</p>
            </div>
            <div className="amenity">
              <h3>Designer Upgrades</h3>
              <p>Quartz countertops, custom cabinetry, hardwood floors, crown molding, and high-end appliances throughout.</p>
            </div>
            <div className="amenity">
              <h3>Smart Home Technology</h3>
              <p>Integrated home automation, security systems, whole-house audio, and energy management systems.</p>
            </div>
            <div className="amenity">
              <h3>Resort-Style Backyards</h3>
              <p>Custom pools, outdoor kitchens, fire pits, and landscaped gardens designed for entertainment and relaxation.</p>
            </div>
            <div className="amenity">
              <h3>Premium Neighborhoods</h3>
              <p>Quiet streets, larger lots, and proximity to top-rated Westminster schools and parks. Many near the Seal Beach border.</p>
            </div>
            <div className="amenity">
              <h3>ADU & Guest Suites</h3>
              <p>Many luxury homes include detached ADUs or separate guest quarters — perfect for extended family or rental income.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Luxury Market Snapshot</h2>
          <p>The luxury segment in Westminster offers premium living at a value compared to neighboring coastal communities.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Luxury Home Price</td><td>~$1,200,000</td></tr>
              <tr><td>Avg Days on Market</td><td>30–45 Days</td></tr>
              <tr><td>Most Active Price Range</td><td>$1M – $1.8M</td></tr>
              <tr><td>Property Types</td><td>Custom SFR, New Construction, Estates</td></tr>
              <tr><td>Key Features</td><td>4+ beds, 3+ baths, 2,500+ sq ft</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Westminster Luxury Properties for Sale</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Loading properties...</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>Listed by: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>Browse all Westminster luxury listings by clicking "Search Listings" below.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Find Your Westminster Luxury Home?</h2>
          <p>Contact Truc Doan for a private showing of Westminster's finest properties. Discreet, professional service.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Westminster Luxury Homes Inquiry" className="cta-button primary">Email Truc Doan</a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">Call +1-818-818-0989</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Listings</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterLuxuryHomesPage;
